import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  'Comanda plasata': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Comanda Acceptata': 'bg-orange-100 text-orange-800 border border-orange-200',
  'Comanda in livrare': 'bg-purple-100 text-purple-800 border border-purple-200',
  'Comanda livrata': 'bg-green-100 text-green-800 border border-green-200'
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      toast.error('Eroare la încărcarea comenzilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
          Comenzile Mele
        </h1>

        {orders.length === 0 ? (
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
            Nu ai comenzi plasate încă.
          </p>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card
                key={order.id}
                className="border border-slate-200 shadow-sm"
                data-testid={`order-${order.id}`}
              >
                <CardHeader className="bg-slate-50 border-b border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle style={{ fontFamily: 'Outfit' }}>
                        Comandă #{order.id.substring(0, 8).toUpperCase()}
                      </CardTitle>
                      <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Plasat la:{' '}
                        {format(new Date(order.created_at), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                      </p>
                    </div>
                    <Badge
                      className={statusStyles[order.status]}
                      data-testid={`order-status-${order.id}`}
                    >
                      {order.status}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="space-y-2 mb-4">
                    {order.items.map((item) => (
                      <div
                        key={item.product_id || item.name}
                        className="flex justify-between text-slate-700"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                      >
                        <span>
                          {item.name} x {item.quantity}
                        </span>
                        <span className="font-semibold">
                          {item.is_quote_only
                            ? <span className="text-orange-600">Cere ofertă</span>
                            : `${(item.price * item.quantity).toFixed(2)} RON`}
                        </span>
                      </div>
                    ))}
                  </div>

                  {order.client_note && (
                    <div
                      className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md"
                      data-testid={`order-client-note-${order.id}`}
                    >
                      <p className="text-xs font-semibold text-blue-900 mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Instrucțiunile tale:
                      </p>
                      <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans' }}>
                        {order.client_note}
                      </p>
                    </div>
                  )}

                  {order.admin_note && (
                    <div
                      className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md"
                      data-testid={`order-admin-note-${order.id}`}
                    >
                      <p className="text-xs font-semibold text-orange-800 mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Mesaj de la TUNA FOOD BBD:
                      </p>
                      <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans' }}>
                        {order.admin_note}
                      </p>
                    </div>
                  )}

                  <div className="border-t pt-4 flex justify-between items-center">
                    <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                      Total:
                    </span>
                    <span
                      className="text-2xl font-bold text-blue-900"
                      style={{ fontFamily: 'Outfit' }}
                      data-testid={`order-total-${order.id}`}
                    >
                      {order.total.toFixed(2)} RON
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;